import { useMemo } from 'react'

import {
  AssignedUserSelector,
  Link,
  StatusBadge,
  Table,
  Tag,
  Text,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { EntitiesState } from '@redux/entities'
import type { TaskListItemModel } from '@models/taskListItem'

import { getColor } from '@functions/status'

import useTaskListItem, { generateUrls, useRelations } from '@hooks/useTaskListItem'

import EntitySelector from '@components/EntitySelector'

const watchEntityKeys = ['statuses', 'taskListItems']

const Title = (props: { taskListItem: TaskListItemModel }) => {
  const { taskListItem } = props

  const { parentTask } = useRelations(taskListItem)
  const { editTaskListItemUrl } = generateUrls(parentTask, { includeRedirect: true })

  return (
    <Link
      href={editTaskListItemUrl}
      textProps={{
        color: 'bodyFontColor', display: 'inline-block', variant: 'ellipsis', width: 320,
      }}
    >
      {taskListItem?.title}
    </Link>
  )
}

const Assignee = (props: { assigneeId: number, entities: EntitiesState, taskListItem: TaskListItemModel }) => {
  const {
    assigneeId, entities, entities: { images, users }, taskListItem,
  } = props

  const assignee = users[assigneeId] || {}
  // @ts-expect-error TS2538
  const image = images[assignee.image] || {}

  const taskListItemPayload = useTaskListItem(taskListItem)
  const { callbacks: { updateTaskListItem } } = taskListItemPayload

  return (
    <AssignedUserSelector
      // @ts-expect-error TS2339
      callbacks={{ selectUser: user => updateTaskListItem({ id: taskListItem.id, assignee_id: user.id }) }}
      entities={entities}
      entitySelectorComponent={EntitySelector}
      userImageKey="image"
      selectedUser={{
        id: assignee.id,
        // @ts-expect-error TS2322
        fullName: assignee.full_name,
        image: {
          url: image.thumb_url,
        },
      }}
    />
  )
}

const buildColumns = (entities: EntitiesState) => {
  const { taskListItems } = entities

  return [
    {
      title: 'Title',
      dataKey: 'title',
      cellProps: {
        verticalAlign: 'middle',
        width: 320,
      },
      // @ts-expect-error TS7006
      render: (_, item: TaskListItemModel) => (
        <Title taskListItem={item} />
      ),
    },
    {
      title: 'Parent Task',
      dataKey: 'parent_task_id',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}

        return (
          <Text color="bodyFontColor" display="inline-block" width={240} style={{ whiteSpace: 'nowrap' }}>
            {parentTask?.title || 'Not Loaded...'}
          </Text>
        )
      },
    },
    {
      title: 'Due Date',
      cellProps: {
        key: 'due_date',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'parent_task_id',
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}
        const dueDate = digObject(parentTask, 'dates.due.date')

        return (
          <Tag
            boxProps={{
              color: dueDate ? null : 'lightGrey',
              display: 'inline-flex',
              fontSize: dueDate ? 'xxsmall' : 'small',
              fontStyle: dueDate ? 'normal' : 'italic',
              paddingX: 'medium',
              variant: dueDate ? 'whiteGrey' : undefined,
            }}
            // @ts-expect-error TS2322
            width="auto"
          >
            {dueDate || 'Not Set'}
          </Tag>
        )
      },
    },
    {
      title: 'Priority',
      cellProps: {
        key: 'priority',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'parent_task_id',
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}
        const priorityKey = digObject(parentTask, 'priority', 'default')

        return <StatusBadge color={getColor('priority', priorityKey)} ghost text={priorityKey} />
      },
    },
    {
      title: 'Assignee',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 91,
      },
      dataKey: 'assignee_id',
      render: (assigneeId: number, taskListItem: TaskListItemModel) => (
        <Assignee assigneeId={assigneeId} entities={entities} taskListItem={taskListItem} />
      ),
    },
  ]
}

interface TasksTableProps {
  entities: EntitiesState,
  taskListItems: TaskListItemModel[],
}

const TasksTable = (props: TasksTableProps) => {
  const {
    entities,
    taskListItems,
  } = props

  const {
    updatedEntities: {
      statuses: statusesUpdatedAt,
      taskListItems: taskListItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const memoColumns = useMemo(
    () => buildColumns(entities),
    [taskListItems.length, statusesUpdatedAt, taskListItemsUpdatedAt],
  )

  return (
    <Table
      boxProps={{ border: 'none', overflowY: 'auto' }}
      // @ts-expect-error TS2322
      columns={memoColumns}
      // @ts-expect-error TS2322
      data={taskListItems}
      stickyHeader
    />
  )
}

export default TasksTable
